import React, { useMemo } from "react";
import {
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  getMarkerEnd,
  MarkerType,
} from "reactflow";
import { Layers, MessageCircle, Sparkles, Zap, CheckCircle2 } from "lucide-react";
import type { GraphCanvasEdgeData, GraphEdgeBadgeCounts } from "lib/api/graphCollections";
import {
  formatGraphMessage,
  graphMessage,
  type GraphLocale,
} from "./graphCopy";

/**
 * Selection state surfaced by the edge-selection reducer onto `edge.data`.
 *  - "none": normal rest state
 *  - "neighborhood": edge is part of the currently-selected node's
 *                    inbound/outbound link set but is not the focused edge
 *  - "focus": edge is the single focused edge (inspector open)
 *  - "dim": edge is NOT part of the neighborhood but another edge is
 *           focused; render faded so the focused neighborhood stays visible
 */
export type GraphEdgeSelectionState = "none" | "neighborhood" | "focus" | "dim";

/**
 * Extra per-edge UI metadata produced by GraphExplorerEdit when decorating
 * edges before handing them to React Flow. Lives on `edge.data` alongside
 * the persistent `GraphCanvasEdgeData` fields so the custom edge can style
 * itself without extra context.
 */
export interface GraphInfluenceEdgeData extends GraphCanvasEdgeData {
  selectionState?: GraphEdgeSelectionState;
  /** True for edges that were just added in this session; drives the "new" highlight. */
  isNew?: boolean;
  /** Remix-only: dashed stroke for user-added bridging edges. */
  isBridge?: boolean;
  /** Locale for badge aria-labels / tooltips. */
  locale?: GraphLocale;
}

/**
 * Palette mirrors the tailwind theme (`eoe-text-heading` / `eoe-dark-text-heading`
 * for the rest state, purple for selection).
 *
 * - `FOCUS_EDGE_STROKE` (animated "marching ants" via `edge.animated=true`)
 *   is applied to the clicked edge so it reads as the one the user is
 *   currently inspecting.
 * - `NEIGHBORHOOD_EDGE_STROKE` (solid) is applied to the other edges touching
 *   the clicked edge's endpoints so the user can trace source/target chains
 *   at a glance.
 * - New edges created in the session inherit the same animated purple so the
 *   "just added" and "just clicked" states read identically — both are ways
 *   of saying "this is the edge the user cares about right now".
 */
const FOCUS_EDGE_STROKE = "#a855f7";
const NEIGHBORHOOD_EDGE_STROKE = "#8b5cf6";

/**
 * Default stroke style skeleton. The `stroke` color is intentionally NOT set
 * here — the default color comes from the `.graph-influence-edge-path` CSS
 * class so light/dark-mode tokens stay the source of truth for the rest
 * state. Inline `stroke` on return values only fires for selected/focused
 * states where we want a specific purple.
 */
const resolveStrokeStyles = (
  selectionState: GraphEdgeSelectionState,
  isNew: boolean,
  isBridge: boolean
): React.CSSProperties => {
  const base: React.CSSProperties = {
    strokeWidth: 2,
    strokeDasharray: isBridge ? "6 4" : undefined,
    opacity: 1,
    transition: "opacity 150ms ease, stroke-width 150ms ease",
  };

  if (selectionState === "dim") {
    base.opacity = 0.28;
    return base;
  }

  if (selectionState === "focus" || isNew) {
    base.stroke = FOCUS_EDGE_STROKE;
    base.strokeWidth = Math.max(Number(base.strokeWidth) || 2, isNew ? 4 : 3.5);
    base.filter = "drop-shadow(0 0 8px rgba(168, 85, 247, 0.6))";
    return base;
  }

  if (selectionState === "neighborhood") {
    base.stroke = NEIGHBORHOOD_EDGE_STROKE;
    base.strokeWidth = Math.max(Number(base.strokeWidth) || 2, 3);
    base.filter = "drop-shadow(0 0 5px rgba(139, 92, 246, 0.35))";
    return base;
  }

  return base;
};

const BADGE_DEFAULT: GraphEdgeBadgeCounts = {
  otherCollections: 0,
  notes: 0,
  impacts: 0,
  solutions: 0,
  actions: 0,
};

interface BadgeChipProps {
  icon: React.ReactNode;
  count: number;
  label: string;
}

const BadgeChip: React.FC<BadgeChipProps> = ({ icon, count, label }) => {
  if (!count) {
    return null;
  }
  return (
    <span
      className="graph-influence-edge-badge inline-flex items-center gap-0.5 rounded-full bg-eoe-bg/90 px-1.5 py-0.5 text-[10px] font-semibold text-eoe-text shadow-sm ring-1 ring-eoe-border-main dark:bg-eoe-dark-bg/90 dark:text-eoe-dark-text dark:ring-eoe-dark-border-main"
      aria-label={label}
      title={label}
    >
      <span aria-hidden className="inline-flex items-center justify-center">
        {icon}
      </span>
      <span className="tabular-nums">{count}</span>
    </span>
  );
};

/**
 * Custom edge for the graph collection editor. Renders a curved bezier path,
 * an invisible thicker hit overlay for easier click on crowded graphs, and a
 * floating badge cluster (other-collections, notes, impacts, solutions,
 * actions) anchored at the midpoint.
 *
 * The edge does NOT handle its own click state; the canvas forwards clicks
 * through React Flow's `onEdgeClick` to the edit surface which drives the
 * two-step selection reducer and passes the resulting `selectionState` back
 * through `edge.data`.
 */
const GraphInfluenceEdge: React.FC<EdgeProps<GraphInfluenceEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  selected,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  // React Flow may set the edge's built-in `selected` class based on click
  // state. We prefer the explicit data.selectionState the editor drives,
  // because it supports the "neighborhood" tier that React Flow does not.
  const selectionState: GraphEdgeSelectionState =
    data?.selectionState
    ?? (selected ? "focus" : "none");
  const isNew = Boolean(data?.isNew);
  const isBridge = Boolean(data?.isBridge);
  const locale: GraphLocale = data?.locale || "en-US";
  const badges = data?.badges || BADGE_DEFAULT;

  const pathStyle = useMemo(
    () => resolveStrokeStyles(selectionState, isNew, isBridge),
    [selectionState, isNew, isBridge]
  );

  // Resolve marker to the string form React Flow expects on the path.
  // `getMarkerEnd` accepts a MarkerType + options and returns e.g.
  // `url(#react-flow__arrowclosed)`. Strings passed in by the caller are
  // forwarded unchanged.
  const markerEndUrl = useMemo(() => {
    if (typeof markerEnd === "string") {
      return markerEnd;
    }
    const spec = markerEnd ?? { type: MarkerType.ArrowClosed, width: 16, height: 16 };
    return getMarkerEnd(spec.type, id);
  }, [markerEnd, id]);

  /*
   * React Flow v11's `BaseEdge` typedef does not expose `className`, so we
   * render the path element directly. We still apply React Flow's canonical
   * `react-flow__edge-path` class so the parent `<g class="react-flow__edge
   * ... animated">` CSS selectors (marching-ants animation when
   * `edge.animated=true`) keep working. Our theme-aware default stroke
   * lives in the companion `.graph-influence-edge-path` CSS class.
   */
  return (
    <>
      <path
        id={`${id}-hit`}
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={18}
        className="graph-influence-edge-hit"
        style={{ pointerEvents: "stroke" }}
      />
      <path
        id={id}
        d={edgePath}
        fill="none"
        className="react-flow__edge-path graph-influence-edge-path"
        style={pathStyle}
        markerEnd={markerEndUrl}
      />
      <EdgeLabelRenderer>
        <div
          className="graph-influence-edge-label-container nodrag nopan absolute pointer-events-none"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            opacity: selectionState === "dim" ? 0.35 : 1,
            transition: "opacity 150ms ease",
          }}
        >
          <div className="graph-influence-edge-badges pointer-events-auto flex items-center gap-1">
            <BadgeChip
              icon={<Layers className="size-2.5" />}
              count={badges.otherCollections}
              label={formatGraphMessage(locale, "otherCollectionsBadge", {
                count: badges.otherCollections,
              })}
            />
            <BadgeChip
              icon={<MessageCircle className="size-2.5" />}
              count={badges.notes}
              label={formatGraphMessage(locale, "notesBadge", {
                count: badges.notes,
              })}
            />
            <BadgeChip
              icon={<Sparkles className="size-2.5" />}
              count={badges.impacts}
              label={formatGraphMessage(locale, "impactsBadge", {
                count: badges.impacts,
              })}
            />
            <BadgeChip
              icon={<CheckCircle2 className="size-2.5" />}
              count={badges.solutions}
              label={formatGraphMessage(locale, "solutionsBadge", {
                count: badges.solutions,
              })}
            />
            <BadgeChip
              icon={<Zap className="size-2.5" />}
              count={badges.actions}
              label={formatGraphMessage(locale, "actionsBadge", {
                count: badges.actions,
              })}
            />
            {isNew ? (
              <span
                className="graph-influence-edge-new inline-flex items-center rounded-full bg-purple-500/90 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow"
                aria-label={graphMessage(locale, "edgeNewBadge")}
              >
                {graphMessage(locale, "edgeNewBadge")}
              </span>
            ) : null}
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default GraphInfluenceEdge;
